import { useContext } from "react";
import { useHistory } from "react-router";
import { ScreenMax } from "styles/antTheme/Variables";
import { useMediaQuery } from "usehooks-ts";

import { FeedbackTarget } from "@btnet/services-btnet/handlers/models/feedbackTarget";

import { BdsButtonGroup, BdsRollupButton } from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { routes } from "utilities/routes";

import FeedbackEmbeddablePanel from "commonComponents/feedback/FeedbackEmbeddablePanel/FeedbackEmbeddablePanel";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";

import { SpecificationBookListToggle } from "./SpecificationBookListToggle";

export type ViewMode = "list" | "book" | "plans";

interface ISpecsPlansViewToggleProps {
    viewMode: ViewMode;
    jobId?: number;
    showFeedback?: boolean;
}

/**
 * Toggle component to switch between Plans, Specifications Book View, and Specifications List View
 * Uses routing to navigate between views. Rendered for all portal types.
 */
export function SpecsPlansViewToggle({
    viewMode: currentViewMode,
    jobId,
    showFeedback = false,
}: Readonly<ISpecsPlansViewToggleProps>) {
    const history = useHistory();
    const userInfoContext = useContext(UserInfoContext);
    const builderInfo = useContext(BuilderInfoContext);
    const useMobileLayout = useMediaQuery(`(max-width: ${ScreenMax.xs}px)`);
    const showPlansSpecsToggle = true;
    // Once SpecsDeprecateListView is enabled, the list view is gone — the book/list
    // toggle always shows the book view, so there is nothing to switch to.
    const specsDeprecateListView = !!builderInfo?.flags.specsDeprecateListView;
    const showBookListToggle =
        !specsDeprecateListView &&
        currentViewMode !== "plans" &&
        userInfoContext?.loginType === BTLoginTypes.BUILDER;
    const feedbackTarget =
        currentViewMode === "plans" ? FeedbackTarget.Plans : FeedbackTarget.Specifications;

    if (!showPlansSpecsToggle && !showBookListToggle) {
        return null;
    }

    const handleViewChange = (newView: string) => {
        if (newView === "book") {
            // Navigate to book view without ID (will auto-select first spec)
            history.push(routes.specification.getBookLink());
        } else if (newView === "plans") {
            // Navigate to plans view
            history.push(routes.plans.getPlansListLink());
        }
    };
    // Create unique IDs based on current view mode to prevent duplicates during route transitions
    const idSuffix =
        currentViewMode === "plans" ? "Plans" : currentViewMode === "book" ? "Book" : "List";

    const toggleButtons = (
        <ShowOnPortal
            builder
            subs
            owner
            render={() => (
                <BdsButtonGroup
                    id={`specsPlansToggle${idSuffix}`}
                    testid="specs-plans-toggle"
                    compact
                    menuTestid={`specsPlansToggle${idSuffix}-menu`}
                    rollupTestid={`specsPlansToggle${idSuffix}Rollup`}
                    selectable
                    flexAlign="start"
                >
                    <BdsRollupButton
                        id={`plansViewSelectButton${idSuffix}`}
                        testid="view-toggle-plans"
                        onClick={() => handleViewChange("plans")}
                        selected={currentViewMode === "plans"}
                        text="Plans"
                        value="plans"
                    />
                    <BdsRollupButton
                        id={`specsViewSelectButton${idSuffix}`}
                        testid="view-toggle-specs"
                        onClick={() => handleViewChange("book")}
                        selected={currentViewMode !== "plans"}
                        text="Specs"
                        value="specifications"
                    />
                </BdsButtonGroup>
            )}
        />
    );

    const feedbackPanel = showFeedback && (
        <FeedbackEmbeddablePanel
            feedbackTarget={feedbackTarget}
            promptText=""
            className=""
            extraInfo={
                currentViewMode === "plans"
                    ? undefined
                    : JSON.stringify({ sourceEntityName: "Specifications", jobId })
            }
        />
    );

    if (useMobileLayout) {
        return (
            <div className="flex flex-col gap-1">
                <div className="flex flex-row grow justify-between">
                    {toggleButtons}
                    {showBookListToggle && (
                        <SpecificationBookListToggle viewMode={currentViewMode} />
                    )}
                </div>
                {feedbackPanel}
            </div>
        );
    }

    return (
        <div className="flex flex-row grow justify-between">
            {toggleButtons}
            <div className="flex flex-row gap-4 items-center">
                {feedbackPanel}
                {showBookListToggle && <SpecificationBookListToggle viewMode={currentViewMode} />}
            </div>
        </div>
    );
}
